export interface UserProfile {
  id: string;
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  role: 'superadmin' | 'installer';
  created_at: string;
  updated_at: string;
}

export interface Brand {
  id: string;
  name: string;
  not_recommended: boolean;
  created_at: string;
  updated_at: string;
}

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// Product type — replaces the legacy `category` column. Tightened controlled
// vocabulary for the rebuilt catalog. The legacy values (`seat replacement
// bidet`, `bidet attachment`, etc.) were renamed to underscored snake_case
// during the catalog-rebuild-v2 migration.
export type ProductType =
  | 'seat_replacement_bidet'
  | 'under_seat_attachment'
  | 'handheld_sprayer'
  | 'integrated_bidet_toilet'
  | 'ceramic_bidet_with_faucet'
  | 'bidet_faucet_only'
  | 'installation_service'
  | 'other';

export type CompatibleToiletShape = 'round' | 'elongated' | 'both' | 'not_applicable';
export type CompatibleToiletHeight = 'standard' | 'chair_height' | 'both' | 'not_applicable';
export type PowerSource = 'none' | 'battery' | 'electric' | 'electric_with_battery_backup';
export type WaterHeatingMethod = 'none' | 'tankless_unlimited' | 'reservoir_limited' | 'requires_hot_water_hookup';
export type NozzleMaterial = 'plastic' | 'stainless_steel' | 'other';
export type PrimaryControlType = 'side_arm_dial' | 'side_panel' | 'wireless_remote' | 'mobile_app';

export interface Product {
  id: string;
  product_id: string;
  brand_id: string;
  brand?: Brand;
  model: string;
  model_name: string | null;
  title: string;
  tier: 'Basic' | 'Mid-Range' | 'High-End' | 'Not a Bidet' | null;
  product_type: ProductType | null;
  subtitle: string | null;
  overview: string | null;
  why_we_love_it: string | null;
  caution: string | null;
  installation_type: string | null;
  live_on_bny: boolean;
  not_listed_on_amazon: boolean;

  // Catalog-rebuild-v2 schema: identity & format
  compatible_toilet_shape: CompatibleToiletShape | null;
  compatible_toilet_height: CompatibleToiletHeight | null;

  // Power & water
  power_source: PowerSource | null;
  water_heating_method: WaterHeatingMethod | null;
  water_temperature_adjustable: boolean | null;

  // Wash configuration
  nozzle_count: number | null;
  nozzle_material: NozzleMaterial | null;
  self_cleaning_nozzle: boolean | null;
  adjustable_nozzle_position: boolean | null;
  adjustable_spray_pressure: boolean | null;

  // Wash modes
  has_feminine_wash: boolean | null;
  has_oscillation: boolean | null;
  has_pulse: boolean | null;
  has_turbo_wash: boolean | null;
  has_pre_rinse: boolean | null;

  // Comfort
  heated_seat: boolean | null;
  warm_air_dryer: boolean | null;

  // Hygiene
  deodorizer: boolean | null;
  uv_sterilization: boolean | null;

  // Controls & presets
  primary_control_type: PrimaryControlType | null;
  has_companion_app: boolean | null;
  programmable_presets: boolean | null;
  night_light: boolean | null;

  // Convenience
  seat_soft_close: boolean | null;
  seat_auto_open: boolean | null;
  seat_auto_close: boolean | null;
  quick_release: boolean | null;

  // Quality & certifications
  warranty_years: number | null;
  warranty_is_lifetime: boolean | null;
  commercial_grade: boolean | null;
  certifications: string[] | null;
  ada_compliant: boolean | null;
  left_handed_friendly: boolean | null;

  // Reference documents (backend use, not customer-facing)
  spec_sheet_url: string | null;
  installation_guide_url: string | null;
  repair_parts_diagram_url: string | null;

  created_at: string;
  updated_at: string;
}

export interface Reseller {
  id: string;
  name: string;
  slug: string;
  website_url: string | null;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductVariationReseller {
  id?: string;
  variation_id: string;
  reseller_id: string;
  price: number | null;
  url: string | null;
  shipping_days: number | null;
  in_stock: boolean | null;
  created_at?: string;
  updated_at?: string;
  // Joined from resellers table
  resellers?: Reseller;
}

export type BnyInstallStatus = 'installable' | 'listed_only' | 'discontinued';

export type BnyNotInstallReason =
  | 'requires_hot_water_hookup'
  | 'commercial_grade_only'
  | 'requires_full_toilet_replacement'
  | 'ceramic_bidet_fixture'
  | 'not_yet_offered'
  | 'other';

export const BNY_NOT_INSTALL_REASON_LABELS: Record<BnyNotInstallReason, string> = {
  requires_hot_water_hookup: "We don't endorse hot-water hookup attachments",
  commercial_grade_only: "We don't currently install commercial-grade fixtures",
  requires_full_toilet_replacement: "We install bidet seats, not full toilets",
  ceramic_bidet_fixture: "For traditional ceramic bidets — separate scope",
  not_yet_offered: "We don't currently install this category",
  other: "Not currently available",
};

export interface ProductVariation {
  id: string;
  product_id: string;
  variation_id: string | null;
  color: string | null;
  style: string | null;
  msrp: number | null;
  brand_url: string | null;
  live_on_bny: boolean;
  is_default_variation: boolean;
  passthrough_tax: boolean;

  // Catalog-rebuild-v2: BNY install support per variation
  bny_install_status: BnyInstallStatus;
  bny_not_install_reason: BnyNotInstallReason | null;
  bny_not_install_note: string | null;

  created_at: string;
  updated_at: string;
}

export interface ProductImage {
  id: string;
  product_id: string;
  variation_id: string | null;
  category: string | null;
  is_main_image: boolean;
  image_url: string | null;
  image_filename: string | null;

  // Catalog-rebuild-v2: image source attribution (restored)
  attribution_url: string | null;
}

export interface ProductWithRelations extends Product {
  brand: Brand;
  variations: ProductVariation[];
  images: ProductImage[];
}

// Feature field names (snake_case) for rendering forms.
// Updated for catalog-rebuild-v2 — these are the boolean feature columns
// that buyers actually weigh when choosing a bidet. The redundant /
// non-decision-driving columns from the old schema have been dropped.
export const FEATURE_FIELDS = [
  // Wash configuration
  'self_cleaning_nozzle',
  'adjustable_nozzle_position',
  'adjustable_spray_pressure',
  // Wash modes
  'has_feminine_wash',
  'has_oscillation',
  'has_pulse',
  'has_turbo_wash',
  'has_pre_rinse',
  // Comfort
  'heated_seat',
  'warm_air_dryer',
  // Hygiene
  'deodorizer',
  'uv_sterilization',
  // Controls
  'has_companion_app',
  'programmable_presets',
  'night_light',
  // Convenience
  'seat_soft_close',
  'seat_auto_open',
  'seat_auto_close',
  'quick_release',
  // Quality
  'warranty_is_lifetime',
  'commercial_grade',
  'ada_compliant',
  'left_handed_friendly',
  'water_temperature_adjustable',
] as const;

// Enum-typed product columns — for the admin form to render dropdowns
// instead of yes/no toggles.
export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  seat_replacement_bidet: 'Seat replacement bidet',
  under_seat_attachment: 'Under-seat attachment',
  handheld_sprayer: 'Handheld sprayer',
  integrated_bidet_toilet: 'Integrated bidet toilet',
  ceramic_bidet_with_faucet: 'Ceramic bidet (with faucet)',
  bidet_faucet_only: 'Bidet faucet only',
  installation_service: 'Installation service',
  other: 'Other',
};

export const POWER_SOURCE_LABELS: Record<PowerSource, string> = {
  none: 'No power required (water pressure only)',
  battery: 'Battery powered',
  electric: 'Electric (mains)',
  electric_with_battery_backup: 'Electric with battery backup',
};

export const WATER_HEATING_METHOD_LABELS: Record<WaterHeatingMethod, string> = {
  none: 'Cold water only',
  tankless_unlimited: 'Tankless / unlimited warm water',
  reservoir_limited: 'Reservoir-based warm water (limited)',
  requires_hot_water_hookup: 'Requires hot-water supply hookup',
};

export const PRIMARY_CONTROL_TYPE_LABELS: Record<PrimaryControlType, string> = {
  side_arm_dial: 'Side-arm dial',
  side_panel: 'Side panel',
  wireless_remote: 'Wireless remote',
  mobile_app: 'Mobile app',
};

export const NOZZLE_MATERIAL_LABELS: Record<NozzleMaterial, string> = {
  plastic: 'Plastic',
  stainless_steel: 'Stainless steel',
  other: 'Other',
};

// --- Order-related types ---

export interface Customer {
  id: string;
  email: string | null;
  phone_number: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface Recipient {
  id: string;
  full_name: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface DiscountCode {
  id: string;
  code: string;
  discount_amount: number;
  discount_type: string | null;
  created_at: string;
  updated_at: string;
}

export interface Order {
  id: string;
  customer_id: string | null;
  recipient_id: string | null;
  discount_code_id: string | null;
  created_at: string;
}

export interface Payment {
  id: string;
  order_id: string;
  stripe_payment_intent_id: string | null;
  stripe_payment_method_id: string | null;
  amount: number | null;
  payment_method: string | null;
  created_at: string;
}

export interface OrderProduct {
  id: string;
  order_id: string;
  product_id: string | null;
  product_variation_id: string | null;
  base_price: number | null;
  tax: number | null;
  amazon_tracking_id: string | null;
  notes: string | null;
  status: 'active' | 'forfeited';
  created_at: string;
}

export interface Installer {
  id: string;
  name: string;
  user_id: string | null;
  created_at: string;
  updated_at: string;
}

export type InstallationStatus =
  | 'scheduled'
  | 'in_route'
  | 'arrived'
  | 'plan_confirmed'
  | 'before_photos'
  | 'in_progress'
  | 'installation_complete'
  | 'media_uploaded'
  | 'ready_for_demo'
  | 'completed'
  | 'forfeited'
  | 'could_not_complete'
  | 'refuse_to_verify';

export const INSTALLATION_STATUS_LABELS: Record<InstallationStatus, string> = {
  scheduled: 'Scheduled',
  in_route: 'In Route',
  arrived: 'Arrived',
  plan_confirmed: 'Plan Confirmed',
  before_photos: 'Before Photos',
  in_progress: 'In Progress',
  installation_complete: 'Installation Complete',
  media_uploaded: 'Media Uploaded',
  ready_for_demo: 'Ready for Demo',
  completed: 'Completed',
  forfeited: 'Forfeited',
  could_not_complete: 'Could Not Complete',
  refuse_to_verify: 'Refused Verification',
};

export const INSTALLATION_STATUS_STYLES: Record<InstallationStatus, { bg: string; text: string }> = {
  scheduled: { bg: 'bg-gray-100', text: 'text-gray-700' },
  in_route: { bg: 'bg-blue-100', text: 'text-blue-700' },
  arrived: { bg: 'bg-blue-100', text: 'text-blue-700' },
  plan_confirmed: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  before_photos: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  installation_complete: { bg: 'bg-purple-100', text: 'text-purple-700' },
  media_uploaded: { bg: 'bg-purple-100', text: 'text-purple-700' },
  ready_for_demo: { bg: 'bg-orange-100', text: 'text-orange-700' },
  completed: { bg: 'bg-green-100', text: 'text-green-700' },
  forfeited: { bg: 'bg-red-100', text: 'text-red-700' },
  could_not_complete: { bg: 'bg-red-100', text: 'text-red-700' },
  refuse_to_verify: { bg: 'bg-red-100', text: 'text-red-700' },
};

export interface Installation {
  id: string;
  installer_id: string | null;
  order_id: string | null;
  window_start: string | null;
  window_end: string | null;
  notes: string | null;
  status: InstallationStatus;
  status_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderProductMedia {
  id: string;
  order_product_id: string;
  storage_path: string;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  created_at: string;
}

export interface OrderWithRelations extends Order {
  customer: Customer | null;
  recipient: Recipient | null;
  discount_code: DiscountCode | null;
  payments: Payment[];
  order_products: (OrderProduct & {
    product: Pick<Product, 'id' | 'title' | 'product_id'> | null;
    product_variation: Pick<ProductVariation, 'id' | 'color' | 'style' | 'variation_id'> | null;
    order_product_media?: OrderProductMedia[];
  })[];
  installations: (Installation & {
    installer: Installer | null;
  })[];
}

export interface CustomerWithOrders extends Customer {
  orders: (Order & {
    recipient: Pick<Recipient, 'full_name'> | null;
    payments: Pick<Payment, 'amount'>[];
  })[];
}

export interface InstallationWithRelations extends Installation {
  installer: Installer | null;
  order: (Order & {
    customer: Pick<Customer, 'first_name' | 'last_name'> | null;
    recipient: Pick<Recipient, 'full_name' | 'city'> | null;
  }) | null;
}

// Human-readable labels for feature fields
export function featureFieldLabel(field: string): string {
  return field
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// --- Audit Log types ---

export type AuditOperation = 'INSERT' | 'UPDATE' | 'DELETE';

export interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  operation: AuditOperation;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  changed_fields: string[] | null;
  user_id: string | null;
  user_email: string | null;
  cascade_group_id: string | null;
  operation_label: string | null;  // e.g., "Delete Order #abc123..."
  undone_at: string | null;
  undone_by: string | null;
  created_at: string;
}

// For grouping in Operations view
export interface AuditLogGroup {
  cascade_group_id: string;
  operation_label: string;
  created_at: string;
  user_email: string | null;
  records: AuditLog[];
  all_undone: boolean;
}

export interface UndoResult {
  success: boolean;
  audit_log_id?: string;
  operation_undone?: AuditOperation;
  table_name?: string;
  record_id?: string;
  restored_count?: number;  // For cascade group undo
  error?: string;
}

// Table name to human-readable display mapping
export const AUDIT_TABLE_LABELS: Record<string, string> = {
  orders: 'Order',
  customers: 'Customer',
  recipients: 'Recipient',
  installations: 'Installation',
  payments: 'Payment',
  order_products: 'Order Product',
  products: 'Product',
  discount_codes: 'Discount Code',
};

// Operation display colors
export const AUDIT_OPERATION_STYLES: Record<AuditOperation, { bg: string; text: string; label: string }> = {
  INSERT: { bg: 'bg-green-100', text: 'text-green-700', label: 'Created' },
  UPDATE: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Updated' },
  DELETE: { bg: 'bg-red-100', text: 'text-red-700', label: 'Deleted' },
};

// --- Form Submissions (Read-Only) ---

export interface JobApplication {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  why_work_here: string | null;
  experience: string | null;
  education: string | null;
  resume_url: string | null;
  other_docs_url: string | null;
  created_at: string;
}

export interface Complaint {
  id: string;
  order_id_text: string | null;
  complaint: string | null;
  email: string | null;
  phone: string | null;
  created_at: string;
}

export interface SurveyResponse {
  id: string;
  email_address: string | null;
  survey_schema: Record<string, unknown> | null;
  survey_response: Record<string, unknown> | null;
  discount_code_id: string | null;
  discount_code?: DiscountCode | null;
  created_at: string;
}

export interface EmailListSubscriber {
  id: string;
  email_address: string | null;
  created_at: string;
}

export interface CancellationRequest {
  id: string;
  name: string | null;
  request_datetime: string | null;
  created_at: string;
}

// --- Marketing ---

export interface QRCode {
  id: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  qr_code_image_url: string | null;
  created_at: string;
}

// --- Operational ---

export interface InstallerSchedule {
  id: string;
  installer_id: string | null;
  installer?: Installer | null;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
}

export interface ProductLine {
  id: string;
  name: string | null;
  brand_id: string | null;
  brand?: Brand | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

// --- Installer Dashboard types ---

export interface InstallationNote {
  id: string;
  installation_id: string;
  author_id: string | null;
  content: string;
  created_at: string;
}

export type InstallationMediaType = 'before_photo' | 'completion_photo' | 'completion_video';

export interface InstallationMedia {
  id: string;
  installation_id: string;
  type: InstallationMediaType;
  storage_path: string;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export type AgreementType =
  | 'order_confirmation'
  | 'add_on_paid'
  | 'add_on_comped'
  | 'completed_satisfaction'
  | 'refuse_satisfaction';

export interface Agreement {
  id: string;
  installation_id: string;
  type: AgreementType;
  content_text: string;
  content_hash: string;
  signer_name: string;
  signer_email: string | null;
  signer_ip: string | null;
  signer_user_agent: string | null;
  signature_data: string;
  signature_method: 'canvas' | 'typed';
  pdf_storage_path: string | null;
  pdf_emailed_at: string | null;
  countersigner_name: string;
  signed_at: string;
  created_at: string;
}

export type InventoryStatus = 'available' | 'reserved' | 'used' | 'returned';

export interface InventoryItem {
  id: string;
  product_id: string;
  product_variation_id: string | null;
  serial_number: string | null;
  installer_id: string | null;
  status: InventoryStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventoryItemWithRelations extends InventoryItem {
  product: Pick<Product, 'id' | 'title' | 'product_id'>;
  product_variation: Pick<ProductVariation, 'id' | 'color' | 'style'> | null;
  installer: Pick<Installer, 'id' | 'name'> | null;
}

export type SupplementalPaymentType = 'paid' | 'comped';
export type SupplementalDeliveryType = 'transferred_now' | 'future_install';
export type SupplementalPaymentStatus = 'pending' | 'paid' | 'failed' | 'not_applicable';

export interface SupplementalOrder {
  id: string;
  installation_id: string;
  payment_type: SupplementalPaymentType;
  delivery_type: SupplementalDeliveryType;
  stripe_payment_intent_id: string | null;
  stripe_payment_method_id: string | null;
  payment_status: SupplementalPaymentStatus;
  payment_link_token: string | null;
  payment_link_sent_to: string | null;
  payment_link_sent_at: string | null;
  agreement_id: string | null;
  subtotal: number;
  tax: number;
  total: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplementalOrderItem {
  id: string;
  supplemental_order_id: string;
  product_id: string;
  product_variation_id: string | null;
  inventory_id: string | null;
  quantity: number;
  unit_price: number;
  tax: number;
  created_at: string;
}

export interface SupplementalOrderItemWithRelations extends SupplementalOrderItem {
  product: Pick<Product, 'id' | 'title' | 'product_id'>;
  product_variation: Pick<ProductVariation, 'id' | 'color' | 'style'> | null;
}

export interface SupplementalOrderWithRelations extends SupplementalOrder {
  items: SupplementalOrderItemWithRelations[];
  agreement: Agreement | null;
}

// --- Founder Program types ---

export type ProposalInvolvementLevel = 'advisory' | 'part_time_lead' | 'full_time_lead';
export type ProposalStatus = 'submitted' | 'under_review' | 'approved' | 'declined' | 'in_progress' | 'completed';

export const PROPOSAL_INVOLVEMENT_LABELS: Record<ProposalInvolvementLevel, string> = {
  advisory: 'Advisory',
  part_time_lead: 'Part-Time Lead',
  full_time_lead: 'Full-Time Lead',
};

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  declined: 'Declined',
  in_progress: 'In Progress',
  completed: 'Completed',
};

export const PROPOSAL_STATUS_STYLES: Record<ProposalStatus, { bg: string; text: string }> = {
  submitted: { bg: 'bg-gray-100', text: 'text-gray-700' },
  under_review: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  approved: { bg: 'bg-green-100', text: 'text-green-700' },
  declined: { bg: 'bg-red-100', text: 'text-red-700' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700' },
  completed: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

export interface FounderProposal {
  id: string;
  installer_id: string;
  title: string;
  summary: string;
  problem: string;
  solution: string;
  involvement_level: ProposalInvolvementLevel;
  status: ProposalStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

// Full installation with all related data (for installer workflow)
export interface InstallationWithFullRelations extends Installation {
  installer: Installer | null;
  order: (Order & {
    customer: Customer | null;
    recipient: Recipient | null;
    discount_code: DiscountCode | null;
    payments: Payment[];
    order_products: (OrderProduct & {
      product: Pick<Product, 'id' | 'title' | 'product_id'> | null;
      product_variation: Pick<ProductVariation, 'id' | 'color' | 'style' | 'variation_id'> | null;
    })[];
  }) | null;
  notes_list: InstallationNote[];
  media: InstallationMedia[];
  agreements: Agreement[];
  supplemental_orders: SupplementalOrderWithRelations[];
}
